/* global BigInt */
import React, { useCallback, useEffect, useState } from 'react';
import { getApi, postApi } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Deposit.css';

/* ─── Constants ─── */
// USDTDistributor contract on BSC (calls transferFrom internally)
const DISTRIBUTOR_CONTRACT = '0x350D3C3Be251ceD15C086E0584655114fC0c9431';
const BSC_CHAIN_ID = '0x38'; // 56 in hex
const USDT_DECIMALS = 18;
const BSCSCAN_URL = 'https://bscscan.com';

const BSC_NETWORK_PARAMS = {
  chainId: BSC_CHAIN_ID,
  chainName: 'BNB Smart Chain Mainnet',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  rpcUrls: ['https://bsc-dataseed1.binance.org/'],
  blockExplorerUrls: ['https://bscscan.com'],
};

/* ─── Pure ABI Helpers (no library needed) ─── */

/**
 * Encode ERC-20 approve(address spender, uint256 amount) calldata
 * selector: 095ea7b3
 */
function encodeApprove(spender, amountWei) {
  const selector = '095ea7b3';
  const paddedAddr = spender.replace('0x', '').toLowerCase().padStart(64, '0');
  const paddedAmt = BigInt(amountWei).toString(16).padStart(64, '0');
  return '0x' + selector + paddedAddr + paddedAmt;
}

/**
 * Encode USDTDistributor.getTokenAddress() → selector: 10fe9ae8
 * Returns the live USDT token address used by the distributor.
 */
function encodeGetTokenAddress() {
  return '0x10fe9ae8';
}

/**
 * Encode USDTDistributor.deposit(uint256 amount, uint256 userId, uint256 depositId) calldata
 * selector: keccak256("deposit(uint256,uint256,uint256)") → 00aeef8a
 */
function encodeDeposit(amountWei, userId, depositId) {
  const selector = '00aeef8a';
  const paddedAmt = BigInt(amountWei).toString(16).padStart(64, '0');
  const paddedUser = BigInt(userId).toString(16).padStart(64, '0');
  const paddedDep = BigInt(depositId).toString(16).padStart(64, '0');
  return '0x' + selector + paddedAmt + paddedUser + paddedDep;
}

/**
 * Generate a unique depositId: timestamp (ms) shifted left by 32 bits + random 32-bit suffix
 * This gives a 64-bit uint safe for Solidity uint256 and globally collision-resistant.
 */
function generateDepositId() {
  const ts = BigInt(Date.now()); // ~42 bits
  const rand = BigInt(Math.floor(Math.random() * 0xFFFFFFFF)); // 32 bits
  return (ts << 32n) | rand;
}

/** Encode ERC-20 balanceOf(address) calldata */
function encodeBalanceOf(addr) {
  const selector = '70a08231'; // keccak256("balanceOf(address)")[0:4]
  const paddedAddr = addr.replace('0x', '').toLowerCase().padStart(64, '0');
  return '0x' + selector + paddedAddr;
}

/** Convert human USDT amount string to BigInt wei (18 decimals) */
function toWei(amount) {
  const str = Number(amount).toFixed(USDT_DECIMALS);
  const [integer, dec = ''] = str.split('.');
  const paddedDec = dec.padEnd(USDT_DECIMALS, '0').slice(0, USDT_DECIMALS);
  return BigInt(integer) * BigInt(10 ** USDT_DECIMALS) + BigInt(paddedDec);
}

/** Convert BigInt wei to human-readable USDT string */
function fromWei(wei) {
  const divisor = BigInt(10 ** USDT_DECIMALS);
  const intPart = wei / divisor;
  const fracPart = wei % divisor;
  const fracStr = fracPart.toString().padStart(USDT_DECIMALS, '0').slice(0, 4);
  return `${intPart}.${fracStr}`;
}

/** Shorten address for display */
function shortAddr(addr) {
  if (!addr) return '';
  return addr.slice(0, 6) + '...' + addr.slice(-4);
}

/* ─── Wallet Options Config ─── */
// getMobileLink() is a function so window.location is read at click-time, not at module load
const WALLET_OPTIONS = [
  {
    id: 'metamask',
    name: 'MetaMask',
    icon: 'bi-wallet-fill',
    color: '#E8831D',
    check: () => window.ethereum?.isMetaMask,
    getMobileLink: () => `https://metamask.app.link/dapp/${window.location.host}${window.location.pathname}`,
    description: 'Browser Extension / Mobile',
  },
  {
    id: 'trust',
    name: 'Trust Wallet',
    icon: 'bi-shield-check',
    color: '#3375BB',
    check: () => window.ethereum?.isTrust || window.ethereum?.isTrustWallet,
    getMobileLink: () => `https://link.trustwallet.com/open_url?coin_id=20000714&url=${encodeURIComponent(window.location.href)}`,
    description: 'Mobile / Browser Extension',
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    icon: 'bi-coin',
    color: '#0052FF',
    check: () => window.ethereum?.isCoinbaseWallet,
    getMobileLink: () => `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(window.location.href)}`,
    description: 'Browser Extension / Mobile',
  },
  {
    id: 'browser',
    name: 'Browser Wallet',
    icon: 'bi-globe2',
    color: '#00FFB3',
    check: () => !!window.ethereum,
    getMobileLink: () => null,
    description: 'Any injected Web3 wallet',
  },
];

/* ─── Main Component ─── */
const Deposit = () => {
  /* ── Wallet state ── */
  const [walletAddress, setWalletAddress] = useState('');
  const [chainId, setChainId] = useState('');
  const [usdtBalance, setUsdtBalance] = useState('');
  const [bnbBalance, setBnbBalance] = useState('');
  const [connectingWallet, setConnectingWallet] = useState('');
  const [showWalletModal, setShowWalletModal] = useState(false);

  /* ── Deposit state ── */
  const [amount, setAmount] = useState('');
  const [txStatus, setTxStatus] = useState('idle'); // idle | approving | depositing | success | fail
  const [txHash, setTxHash] = useState('');
  const [approveTxHash, setApproveTxHash] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* ── Contract state ── */
  const [usdtContractAddress, setUsdtContractAddress] = useState('');
  const [userId, setUserId] = useState(null); // backend user numeric ID

  /* ── Deposit history state ── */
  const [depositsData, setDepositsData] = useState({ deposits: [], total: 0, current_page: 1 });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);

  const isOnBSC = chainId === BSC_CHAIN_ID;
  const isConnected = !!walletAddress;
  const canDeposit = isConnected && isOnBSC && !!amount && Number(amount) > 0 && !!userId;

  /* ── Lifecycle ── */
  useEffect(() => {
    fetchDeposits();
    fetchUserProfile();
    checkExistingConnection();

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Auto-refresh balances + fetch token address when connected on BSC ── */
  useEffect(() => {
    if (walletAddress && isOnBSC) {
      fetchBalances(walletAddress);
      fetchTokenAddress();
    }
  }, [walletAddress, isOnBSC]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Fetch user profile to get numeric userId ── */
  const fetchUserProfile = async () => {
    try {
      const res = await getApi('profile');
      // Backend returns USER.id as the numeric user ID
      const user = res.data?.USER || res.data?.data;
      if (user?.id) {
        setUserId(Number(user.id));
      } else {
        console.warn('Could not resolve userId from profile response');
      }
    } catch (e) {
      console.error('Profile fetch error:', e);
    }
  };

  /* ── Fetch live USDT token address from USDTDistributor contract ── */
  const fetchTokenAddress = async () => {
    if (!window.ethereum) return;
    try {
      const result = await window.ethereum.request({
        method: 'eth_call',
        params: [{ to: DISTRIBUTOR_CONTRACT, data: encodeGetTokenAddress() }, 'latest'],
      });
      if (result && result !== '0x') {
        // Decode address: last 20 bytes of 32-byte ABI-encoded return
        const addr = '0x' + result.slice(-40);
        setUsdtContractAddress(addr);
      }
    } catch (e) {
      console.error('getTokenAddress() call failed:', e);
    }
  };

  /* ── API: Fetch deposit history ── */
  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const response = await getApi('deposits', { page: 1, per_page: 10 });
      if (response?.data?.status === true) {
        setDepositsData(response.data.data);
      }
    } catch (e) {
      console.error('fetchDeposits error:', e);
    } finally {
      setLoading(false);
    }
  };

  /* ── Wallet: Check if already connected ── */
  const checkExistingConnection = async () => {
    if (!window.ethereum) return;
    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        setWalletAddress(accounts[0]);
        const cid = await window.ethereum.request({ method: 'eth_chainId' });
        setChainId(cid);
      }
    } catch (e) {
      console.error(e);
    }
  };

  /* ── Wallet: Event handlers ── */
  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      setWalletAddress(accounts[0]);
    }
  };

  const handleChainChanged = (cid) => {
    setChainId(cid);
    if (cid !== BSC_CHAIN_ID) {
      setUsdtBalance('');
      setBnbBalance('');
    }
  };

  /* ── Wallet: Fetch balances ── */
  const fetchBalances = useCallback(async (address) => {
    if (!window.ethereum || !address) return;
    try {
      // BNB balance
      const bnbHex = await window.ethereum.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      });
      const bnb = Number(BigInt(bnbHex)) / 1e18;
      setBnbBalance(bnb.toFixed(4));

      // USDT balance via eth_call (use live address from contract, fallback to known BEP-20)
      const usdtAddr = usdtContractAddress || '0x55d398326f99059fF775485246999027B3197955';
      const callData = encodeBalanceOf(address);
      const result = await window.ethereum.request({
        method: 'eth_call',
        params: [{ to: usdtAddr, data: callData }, 'latest'],
      });
      if (result && result !== '0x') {
        const balWei = BigInt(result);
        setUsdtBalance(fromWei(balWei));
      } else {
        setUsdtBalance('0.0000');
      }
    } catch (e) {
      console.error('Balance fetch error:', e);
      setUsdtBalance('0.0000');
    }
  }, []);

  /* ── Wallet: Switch to BSC ── */
  const switchToBSC = async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: BSC_CHAIN_ID }],
      });
    } catch (err) {
      // Chain not added — add it
      if (err.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [BSC_NETWORK_PARAMS],
          });
        } catch (addErr) {
          toast.error('Failed to add BSC network. Please add it manually.');
        }
      } else {
        toast.error('Failed to switch network.');
      }
    }
  };

  /* ── Wallet: Connect ── */
  const connectWallet = async (option) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // If no window.ethereum and mobile, open deep link
    if (!window.ethereum) {
      const deepLink = option.getMobileLink();
      if (isMobile && deepLink) {
        window.open(deepLink, '_blank');
        toast.info(`Opening ${option.name}...`);
      } else {
        toast.error(`${option.name} not detected. Please install the extension.`);
      }
      return;
    }

    setConnectingWallet(option.id);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setWalletAddress(accounts[0]);

      const cid = await window.ethereum.request({ method: 'eth_chainId' });
      setChainId(cid);

      if (cid !== BSC_CHAIN_ID) {
        await switchToBSC();
      }

      setShowWalletModal(false);
      toast.success(`Wallet connected: ${shortAddr(accounts[0])}`);
    } catch (err) {
      if (err.code === 4001) {
        toast.error('Connection rejected. Please approve in your wallet.');
      } else {
        toast.error('Connection failed. Try again.');
      }
    } finally {
      setConnectingWallet('');
    }
  };

  /* ── Wallet: Disconnect ── */
  const disconnectWallet = () => {
    setWalletAddress('');
    setChainId('');
    setUsdtBalance('');
    setBnbBalance('');
    setAmount('');
    setTxStatus('idle');
    setTxHash('');
    toast.info('Wallet disconnected.');
  };

  /* ── Copy address ── */
  const copyAddress = (addr) => {
    navigator.clipboard.writeText(addr).catch(() => { });
    setCopied(true);
    toast.success('Address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  /* ── Deposit: 3-step flow via USDTDistributor contract ──────────────────
   *
   * Step 1: approve(DISTRIBUTOR_CONTRACT, amount) on the USDT token
   *         → lets the distributor call transferFrom on your behalf
   *
   * Step 1.5: POST /start-deposit to register the deposit on the backend
   *           → returns a unique deposit ID (response.data.data.id)
   *
   * Step 2: deposit(amountWei, userId, depositId) on the distributor
   *         → depositId = backend id (guaranteed unique, matches contract mapping)
   * ─────────────────────────────────────────────────────────────────────── */
  const handleDeposit = async () => {
    if (!canDeposit) return;
    if (!userId) { toast.error('User ID not loaded. Please refresh the page.'); return; }

    const numAmount = Number(amount);
    if (numAmount < 10) { toast.error('Minimum deposit is 10 USDT'); return; }

    const usdtBal = Number(usdtBalance);
    if (numAmount > usdtBal) { toast.error('Insufficient USDT balance'); return; }

    // Determine which USDT contract to approve:
    // prefer the live address fetched from the distributor, fall back to known BEP-20 address
    const tokenContract = usdtContractAddress || '0x55d398326f99059fF775485246999027B3197955';

    setSubmitting(true);
    setApproveTxHash('');
    setTxHash('');
    setTxStatus('approving');

    try {
      const amountWei = toWei(amount);

      // ── Step 1: Approve ──────────────────────────────────────────────────
      toast.info('Step 1/2: Approve USDT spend — confirm in your wallet…', { autoClose: 6000 });

      const approveData = encodeApprove(DISTRIBUTOR_CONTRACT, amountWei);
      const approveTx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [{ from: walletAddress, to: tokenContract, data: approveData }],
      });
      setApproveTxHash(approveTx);
      toast.success('Approval submitted!', { autoClose: 3000 });

      // ── Step 1.5: Register deposit on backend → get canonical depositId ──
      let backendDepositId;
      try {
        const startForm = new FormData();
        startForm.append('deposit_amount', amount);
        const startRes = await postApi('start-deposit', startForm);
        if (startRes?.data?.status && startRes?.data?.data?.id) {
          backendDepositId = startRes.data.data.id;
          toast.info(`Deposit registered (ID: ${backendDepositId}). Confirming on-chain…`, { autoClose: 4000 });
        } else {
          throw new Error(startRes?.data?.message || 'start-deposit API did not return an ID');
        }
      } catch (apiErr) {
        toast.error('Failed to register deposit with server. Aborting.');
        setTxStatus('fail');
        setSubmitting(false);
        return;
      }

      // Give the chain a moment (approval may still be pending in mempool)
      await new Promise(r => setTimeout(r, 2000));

      // ── Step 2: Deposit on USDTDistributor ───────────────────────────────
      setTxStatus('depositing');
      toast.info('Step 2/2: Confirm deposit transaction in your wallet…', { autoClose: 6000 });

      const depositData = encodeDeposit(amountWei, userId, backendDepositId);
      const depositTxHash = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [{ from: walletAddress, to: DISTRIBUTOR_CONTRACT, data: depositData }],
      });

      setTxHash(depositTxHash);
      setTxStatus('success');
      toast.success('Deposit submitted successfully! 🎉');

      // Refresh wallet balances after tx
      setTimeout(() => fetchBalances(walletAddress), 5000);

      // ── Update deposit record with on-chain tx hash then refresh list ─────
      try {
        const updateForm = new FormData();
        updateForm.append('deposit_id',     backendDepositId);
        updateForm.append('deposit_amount', amount);
        updateForm.append('from_address',   walletAddress);
        updateForm.append('txn_hash',       depositTxHash);
        await postApi('update-deposit', updateForm);
        await fetchDeposits();
      } catch (apiErr) {
        console.warn('update-deposit API failed (deposit was submitted on-chain):', apiErr);
        // Still refresh history so the pending record shows
        fetchDeposits();
      }

    } catch (err) {
      setTxStatus('fail');
      if (err.code === 4001) {
        toast.error('Transaction rejected in wallet.');
      } else {
        toast.error(err.message || 'Transaction failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openDetails = (dep) => { setSelectedDeposit(dep); setShowDetailsModal(true); };

  const deposits = depositsData?.deposits || [];
  const successDeposits = deposits.filter(d => d.status === 'success');
  const pendingDeposits  = deposits.filter(d => d.status === 'pending');
  const totalDeposited   = successDeposits.reduce((s, d) => s + Number(d.deposit_amount || 0), 0);

  const analytics = [
    { title: 'Total Deposited',  value: `$${totalDeposited.toFixed(2)}`, icon: 'bi-arrow-down-circle', color: 'green' },
    { title: 'Total Records',    value: depositsData?.total || 0,        icon: 'bi-layers',            color: 'blue' },
    { title: 'Pending Deposits', value: pendingDeposits.length,          icon: 'bi-clock',             color: 'yellow' },
  ];

  const QUICK_AMOUNTS = [50, 100, 500, 1000];

  /* ─── Render ─── */
  return (
    <div className="page-wrapper dep-wrapper">
      <ToastContainer position="top-right" autoClose={4000} theme="dark" />

      {/* ── Page Header ── */}
      <div className="page-header mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <div className="section-label">Funds</div>
          <h2 className="page-title">Deposit</h2>
          <p className="page-subtitle">Connect your wallet and deposit USDT (BEP-20) to start earning</p>
        </div>
        <div className="dep-header-badges">
          <span className="dep-network-badge">
            <span className="dep-network-dot"></span>
            BSC Mainnet
          </span>
          <span className="dep-token-badge">
            <i className="bi bi-coin"></i>
            USDT BEP-20
          </span>
        </div>
      </div>

      {/* ── Analytics Cards ── */}
      <div className="row g-3 mb-4">
        {analytics.map((item, i) => (
          <div key={i} className="col-md-4">
            {loading
              ? <div className="skeleton" style={{ height: 90, borderRadius: 18 }}></div>
              : (
                <div className={`metric-card ${item.color}`}>
                  <div className={`metric-icon ${item.color}`}><i className={`bi ${item.icon}`}></i></div>
                  <div>
                    <div className="metric-label">{item.title}</div>
                    <div className="metric-value">{item.value}</div>
                  </div>
                </div>
              )
            }
          </div>
        ))}
      </div>

      {/* ── Main Grid ── */}
      <div className="row g-4 mb-4">

        {/* ── LEFT: Wallet Panel ── */}
        <div className="col-lg-5">
          <div className="dep-wallet-card">

            {/* Not Connected */}
            {!isConnected ? (
              <div className="dep-connect-state">
                <div className="dep-wallet-icon-wrap">
                  <div className="dep-wallet-icon-ring"></div>
                  <i className="bi bi-wallet2 dep-wallet-icon-main"></i>
                </div>
                <h5 className="dep-connect-title">Connect Your Wallet</h5>
                <p className="dep-connect-sub">
                  Connect any BSC-compatible Web3 wallet to deposit USDT BEP-20 and start earning.
                </p>

                <div className="dep-wallet-grid">
                  {WALLET_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      className={`dep-wallet-opt ${connectingWallet === opt.id ? 'connecting' : ''}`}
                      style={{ '--wallet-color': opt.color }}
                      onClick={() => connectWallet(opt)}
                      disabled={!!connectingWallet}
                    >
                      <div className="dep-wallet-opt-icon">
                        <i className={`bi ${opt.icon}`} style={{ color: opt.color }}></i>
                      </div>
                      <div className="dep-wallet-opt-info">
                        <span className="dep-wallet-opt-name">{opt.name}</span>
                        <span className="dep-wallet-opt-desc">{opt.description}</span>
                      </div>
                      {connectingWallet === opt.id
                        ? <span className="spinner-border spinner-border-sm dep-wallet-spinner"></span>
                        : <i className="bi bi-chevron-right dep-wallet-opt-arrow"></i>
                      }
                    </button>
                  ))}
                </div>

                <div className="dep-mobile-note">
                  <i className="bi bi-phone me-2"></i>
                  On mobile? Open this page inside Trust Wallet or MetaMask app browser
                </div>
              </div>

            ) : (
              /* Connected */
              <div className="dep-connected-state">
                {/* Network badge */}
                {!isOnBSC ? (
                  <div className="dep-wrong-network">
                    <div className="dep-wrong-network-icon">
                      <i className="bi bi-exclamation-triangle-fill"></i>
                    </div>
                    <div>
                      <div className="dep-wrong-network-title">Wrong Network</div>
                      <div className="dep-wrong-network-sub">Please switch to BSC Mainnet</div>
                    </div>
                    <button className="dep-switch-btn" onClick={switchToBSC}>
                      Switch
                    </button>
                  </div>
                ) : (
                  <div className="dep-correct-network">
                    <span className="dep-network-dot"></span>
                    <span>BNB Smart Chain Mainnet</span>
                    <span className="dep-chain-id">Chain 56</span>
                  </div>
                )}

                {/* Wallet address */}
                <div className="dep-address-card">
                  <div className="dep-address-avatar">
                    <i className="bi bi-wallet2"></i>
                  </div>
                  <div className="dep-address-info">
                    <div className="dep-address-label">Connected Wallet</div>
                    <div className="dep-address-value">{shortAddr(walletAddress)}</div>
                  </div>
                  <button
                    className="dep-address-copy"
                    onClick={() => copyAddress(walletAddress)}
                    title="Copy full address"
                  >
                    <i className={`bi ${copied ? 'bi-check2' : 'bi-copy'}`}></i>
                  </button>
                  <button className="dep-disconnect-btn" onClick={disconnectWallet} title="Disconnect">
                    <i className="bi bi-power"></i>
                  </button>
                </div>

                {/* Balances */}
                <div className="dep-balances">
                  <div className="dep-balance-item dep-balance-usdt">
                    <div className="dep-balance-icon">
                      <i className="bi bi-coin"></i>
                    </div>
                    <div>
                      <div className="dep-balance-amount">{usdtBalance || '—'}</div>
                      <div className="dep-balance-label">USDT Balance</div>
                    </div>
                  </div>
                  <div className="dep-balance-item dep-balance-bnb">
                    <div className="dep-balance-icon dep-balance-icon-bnb">
                      <i className="bi bi-currency-bitcoin"></i>
                    </div>
                    <div>
                      <div className="dep-balance-amount">{bnbBalance || '—'}</div>
                      <div className="dep-balance-label">BNB (Gas)</div>
                    </div>
                  </div>
                </div>

                {/* Contract info */}
                <div className="dep-recipient-box">
                  <div className="dep-recipient-label">
                    <i className="bi bi-file-code me-1"></i>
                    Distributor Contract
                  </div>
                  <div className="dep-recipient-addr">
                    <span>{shortAddr(DISTRIBUTOR_CONTRACT)}</span>
                    <button onClick={() => copyAddress(DISTRIBUTOR_CONTRACT)} title="Copy contract address">
                      <i className="bi bi-copy"></i>
                    </button>
                  </div>
                  <div className="dep-recipient-full">{DISTRIBUTOR_CONTRACT}</div>
                  {usdtContractAddress && (
                    <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--page-accent)', fontWeight: 600 }}>Token:</span>{' '}
                      {shortAddr(usdtContractAddress)}
                      {' '}(live from contract)
                    </div>
                  )}
                  {userId && (
                    <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--page-accent)', fontWeight: 600 }}>User ID:</span>{' '}
                      #{userId}
                    </div>
                  )}
                </div>

                {/* Refresh balances */}
                <button className="dep-refresh-btn" onClick={() => fetchBalances(walletAddress)}>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh Balances
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Deposit Form ── */}
        <div className="col-lg-7">
          <div className="dep-form-card">
            <div className="dep-form-header">
              <div>
                <p className="dep-form-label">Amount</p>
                <h5 className="dep-form-title">Deposit USDT</h5>
              </div>
              {isConnected && isOnBSC && (
                <div className="dep-form-bal-pill">
                  Bal: <strong>{usdtBalance || '0'} USDT</strong>
                </div>
              )}
            </div>

            {/* Amount Input */}
            <div className={`dep-amount-wrap ${!isConnected ? 'dep-locked' : ''}`}>
              <div className="dep-amount-prefix">USDT</div>
              <input
                type="number"
                className="dep-amount-input"
                placeholder={isConnected ? "Enter amount (min. 10 USDT)" : "Connect wallet first"}
                value={amount}
                onChange={e => {
                  setAmount(e.target.value);
                  setTxStatus('idle');
                }}
                disabled={!isConnected || !isOnBSC || submitting}
                min="10"
                step="1"
              />
              {isConnected && usdtBalance && (
                <button
                  className="dep-max-btn"
                  onClick={() => setAmount(Math.floor(Number(usdtBalance)).toString())}
                  disabled={submitting}
                >
                  MAX
                </button>
              )}
            </div>

            {/* Quick Amount Buttons */}
            <div className="dep-quick-amounts">
              {QUICK_AMOUNTS.map(q => (
                <button
                  key={q}
                  className={`dep-quick-btn ${amount === q.toString() ? 'active' : ''}`}
                  onClick={() => { setAmount(q.toString()); setTxStatus('idle'); }}
                  disabled={!isConnected || !isOnBSC || submitting}
                >
                  ${q}
                </button>
              ))}
            </div>

            {/* Amount Summary */}
            {amount && Number(amount) > 0 && (
              <div className="dep-summary">
                <div className="dep-summary-row">
                  <span>Deposit Amount</span>
                  <strong>{amount} USDT</strong>
                </div>
                <div className="dep-summary-row">
                  <span>Network</span>
                  <strong>BSC Mainnet (BEP-20)</strong>
                </div>
                <div className="dep-summary-row">
                  <span>Step 1</span>
                  <strong className="dep-gas-note">Approve USDT spend</strong>
                </div>
                <div className="dep-summary-row">
                  <span>Step 2</span>
                  <strong className="dep-gas-note">Deposit to distributor</strong>
                </div>
                <div className="dep-summary-row">
                  <span>Gas Fee</span>
                  <strong className="dep-gas-note">≈ 0.002 BNB (2 transactions)</strong>
                </div>
                {userId && (
                  <div className="dep-summary-row">
                    <span>Your User ID</span>
                    <strong style={{ fontFamily: 'JetBrains Mono, monospace' }}>#{userId}</strong>
                  </div>
                )}
                <div className="dep-summary-divider"></div>
                <div className="dep-summary-row dep-summary-total">
                  <span>You Send</span>
                  <strong>{amount} USDT</strong>
                </div>
              </div>
            )}

            {/* Transaction Status */}
            {txStatus === 'approving' && (
              <div className="dep-tx-status dep-tx-pending">
                <div className="dep-tx-spinner">
                  <div className="spinner-border" style={{ width: 28, height: 28, borderWidth: 3 }}></div>
                </div>
                <div>
                  <div className="dep-tx-title">Step 1/2 — Approving USDT Spend…</div>
                  <div className="dep-tx-sub">Confirm the approve transaction in your wallet.</div>
                </div>
              </div>
            )}

            {txStatus === 'depositing' && (
              <div className="dep-tx-status dep-tx-pending">
                <div className="dep-tx-spinner">
                  <div className="spinner-border" style={{ width: 28, height: 28, borderWidth: 3 }}></div>
                </div>
                <div>
                  <div className="dep-tx-title">Step 2/2 — Sending Deposit…</div>
                  <div className="dep-tx-sub">Confirm the deposit transaction in your wallet.</div>
                  {approveTxHash && (
                    <div style={{ fontSize: '0.75rem', marginTop: 4, color: '#22c55e' }}>
                      ✓ Approved:{' '}
                      <a href={`${BSCSCAN_URL}/tx/${approveTxHash}`} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#22c55e' }}>{shortAddr(approveTxHash)}</a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {txStatus === 'success' && txHash && (
              <div className="dep-tx-status dep-tx-success">
                <div className="dep-tx-icon-wrap dep-tx-icon-ok">
                  <i className="bi bi-check-circle-fill"></i>
                </div>
                <div className="dep-tx-info">
                  <div className="dep-tx-title">Deposit Submitted! 🎉</div>
                  {approveTxHash && (
                    <div className="dep-tx-sub" style={{ marginBottom: 4 }}>
                      Approve:{' '}
                      <a href={`${BSCSCAN_URL}/tx/${approveTxHash}`} target="_blank" rel="noopener noreferrer"
                        className="dep-tx-hash">{shortAddr(approveTxHash)}</a>
                    </div>
                  )}
                  <div className="dep-tx-sub">
                    Deposit: <span className="dep-tx-hash">{shortAddr(txHash)}</span>
                  </div>
                  <a
                    className="dep-bscscan-link"
                    href={`${BSCSCAN_URL}/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="bi bi-box-arrow-up-right me-1"></i>
                    View Deposit on BscScan
                  </a>
                </div>
              </div>
            )}

            {txStatus === 'fail' && (
              <div className="dep-tx-status dep-tx-fail">
                <div className="dep-tx-icon-wrap dep-tx-icon-fail">
                  <i className="bi bi-x-circle-fill"></i>
                </div>
                <div>
                  <div className="dep-tx-title">Transaction Failed</div>
                  <div className="dep-tx-sub">Please check your balance and try again.</div>
                </div>
              </div>
            )}

            {/* Deposit Button */}
            {!isConnected ? (
              <button
                className="dep-main-btn dep-main-btn-connect"
                onClick={() => setShowWalletModal(true)}
              >
                <i className="bi bi-wallet2 me-2"></i>
                Connect Wallet to Deposit
              </button>
            ) : !isOnBSC ? (
              <button className="dep-main-btn dep-main-btn-switch" onClick={switchToBSC}>
                <i className="bi bi-arrow-repeat me-2"></i>
                Switch to BSC Mainnet
              </button>
            ) : (
              <button
                className="dep-main-btn dep-main-btn-deposit"
                onClick={handleDeposit}
                disabled={!canDeposit || submitting}
              >
                {submitting
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending Transaction…</>
                  : <><i className="bi bi-arrow-down-circle me-2"></i>Deposit {amount ? `${amount} USDT` : 'USDT'}</>
                }
              </button>
            )}

            {/* Security note */}
            <div className="dep-security-note">
              <i className="bi bi-shield-lock me-2"></i>
              Secured by BSC blockchain. Transaction is irreversible — verify address before confirming.
            </div>

            {/* How It Works */}
            <div className="dep-steps">
              {[
                { n: 1, icon: 'bi-wallet2', text: 'Connect your BSC wallet' },
                { n: 2, icon: 'bi-arrow-left-right', text: 'Network auto-switches to BSC' },
                { n: 3, icon: 'bi-input-cursor-text', text: 'Enter your USDT deposit amount' },
                { n: 4, icon: 'bi-shield-check', text: 'Approve USDT spend (Tx 1)' },
                { n: 5, icon: 'bi-check2-circle', text: 'Confirm deposit (Tx 2)' },
              ].map(s => (
                <div key={s.n} className="dep-step-item">
                  <div className="dep-step-num">
                    <i className={`bi ${s.icon}`}></i>
                  </div>
                  <span>{s.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Deposit History ── */}
      <div className="p-card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ fontWeight: 700, margin: 0 }}>Deposit History</h5>
          <button
            onClick={fetchDeposits}
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '0.3rem 0.75rem', color: 'white', fontSize: '0.82rem', cursor: 'pointer' }}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
          </button>
        </div>
        <div className="p-table-wrap">
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-success"></div></div>
          ) : deposits.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><i className="bi bi-arrow-down-circle"></i></div>
              <h6>No Deposits Yet</h6>
              <p>Your deposit history will appear here</p>
            </div>
          ) : (
            <table className="p-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>ID</th>
                  <th>Requested</th>
                  <th>Deposited</th>
                  <th>Asset</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((dep, index) => (
                  <tr key={dep.id}>
                    <td className="dim">{index + 1}</td>
                    <td className="mono" style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{dep.txn_id}</td>
                    <td style={{ fontWeight: 700, color: 'var(--page-accent)' }}>${dep.request_amount}</td>
                    <td className="mono">${dep.deposit_amount}</td>
                    <td>{dep.deposit_asset}</td>
                    <td>
                      <span className={`s-pill ${
                        dep.status === 'success' ? 'success' :
                        dep.status === 'pending' ? 'pending' : 'active'
                      }`}>
                        {dep.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm"
                        onClick={() => openDetails(dep)}
                        style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: '0.78rem' }}
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Wallet Selection Modal (secondary trigger) ── */}
      {showWalletModal && (
        <div className="dep-modal-bg" onClick={() => setShowWalletModal(false)}>
          <div className="dep-modal" onClick={e => e.stopPropagation()}>
            <div className="dep-modal-header">
              <h5>Select Wallet</h5>
              <button className="dep-modal-close" onClick={() => setShowWalletModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="dep-modal-body">
              {WALLET_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  className={`dep-wallet-opt ${connectingWallet === opt.id ? 'connecting' : ''}`}
                  style={{ '--wallet-color': opt.color }}
                  onClick={() => { setShowWalletModal(false); connectWallet(opt); }}
                  disabled={!!connectingWallet}
                >
                  <div className="dep-wallet-opt-icon">
                    <i className={`bi ${opt.icon}`} style={{ color: opt.color }}></i>
                  </div>
                  <div className="dep-wallet-opt-info">
                    <span className="dep-wallet-opt-name">{opt.name}</span>
                    <span className="dep-wallet-opt-desc">{opt.description}</span>
                  </div>
                  {connectingWallet === opt.id
                    ? <span className="spinner-border spinner-border-sm dep-wallet-spinner"></span>
                    : <i className="bi bi-chevron-right dep-wallet-opt-arrow"></i>
                  }
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Deposit Details Modal ── */}
      {showDetailsModal && selectedDeposit && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#0a1628', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 24 }}>
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="fw-700 mb-0" style={{ color: 'white' }}>
                  <i className="bi bi-receipt me-2" style={{ color: 'var(--page-accent)' }}></i>Deposit Details
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body px-4 py-3">
                {[
                  { label: 'Txn ID',          value: selectedDeposit.txn_id,                                   color: 'var(--text-secondary)' },
                  { label: 'Requested',       value: `$${selectedDeposit.request_amount}`,                    color: 'var(--page-accent)' },
                  { label: 'Deposited',       value: `$${selectedDeposit.deposit_amount}`,                    color: 'var(--page-accent)' },
                  { label: 'Asset',           value: selectedDeposit.deposit_asset,                           color: null },
                  { label: 'Status',          value: selectedDeposit.status,                                  color: selectedDeposit.status === 'success' ? '#22c55e' : '#f59e0b' },
                  { label: 'From',            value: selectedDeposit.from_address || '—',                     color: null },
                  { label: 'To',              value: selectedDeposit.to_address   || '—',                     color: null },
                  { label: 'Txn Hash',        value: selectedDeposit.txn_hash     || '—',                     color: null },
                  { label: 'Available Bal',   value: `$${selectedDeposit.available_amount}`,                  color: 'var(--page-blue)' },
                  { label: 'Date',            value: selectedDeposit.date_time,                               color: null },
                ].map((d, i) => (
                  <div key={i} className="info-row">
                    <span className="i-label">{d.label}</span>
                    <span className="i-value" style={{ color: d.color || 'white', fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all', fontSize: '0.78rem' }}>{d.value}</span>
                  </div>
                ))}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                {selectedDeposit.txn_hash && (
                  <a
                    href={`${BSCSCAN_URL}/tx/${selectedDeposit.txn_hash}`}
                    target="_blank" rel="noopener noreferrer"
                    className="btn w-100 mb-2"
                    style={{ background: 'rgba(0,255,149,0.08)', color: 'var(--page-accent)', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 12, padding: '0.7rem', textDecoration: 'none', display: 'block', textAlign: 'center' }}
                  >
                    <i className="bi bi-box-arrow-up-right me-1"></i>View on BscScan
                  </a>
                )}
                <button
                  className="btn w-100"
                  onClick={() => setShowDetailsModal(false)}
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '0.75rem' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposit;
